import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import AdminLayout from './AdminLayout';

import { API, STEPS } from './setup/setupConstants';
import { StepIndicator } from './setup/StepIndicator';
import { StepCompanyDetails } from './setup/StepCompanyDetails';
import { StepDomainIndustry } from './setup/StepDomainIndustry';
import { StepExtensions } from './setup/StepExtensions';
import { StepNotifications } from './setup/StepNotifications';

const EMPTY_COMPANY = {
  name: '', abn: '', address: '', contact_name: '', contact_phone: '', contact_email: '', website: '',
};

const DEFAULT_FORM = {
  company: EMPTY_COMPANY,
  yabbit_domain: '',
  reseller_key: '',
  reseller_url: '',
  industry: 'car_dealership',
  extensions_monitored: [],
  extension_classifications: {},
  notification_emails: [],
};

const DomainSetupPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { domainId } = useParams();
  const isEdit = Boolean(domainId);
  const headers = { Authorization: `Bearer ${token}` };

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [loadingExtensions, setLoadingExtensions] = useState(false);
  const [yabbitDomains, setYabbitDomains] = useState([]);
  const [availableExtensions, setAvailableExtensions] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);

  useEffect(() => {
    if (!isEdit) return;
    axios.get(`${API}/admin/domains/${domainId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const d = r.data;
        setForm({
          company: d.company || EMPTY_COMPANY,
          yabbit_domain: d.yabbit_domain || '',
          reseller_key: d.reseller_key || '',
          reseller_url: d.reseller_url || '',
          industry: d.industry || 'car_dealership',
          extensions_monitored: d.extensions_monitored || [],
          extension_classifications: d.extension_classifications || {},
          notification_emails: d.notification_emails || [],
        });
      })
      .catch(() => toast.error('Failed to load domain'));
  }, [domainId, isEdit, token]);

  const loadYabbitDomains = async () => {
    setLoadingDomains(true);
    try {
      const r = await axios.get(`${API}/yabbit/domains-list`, { headers });
      setYabbitDomains(r.data.domains || []);
    } catch {
      toast.error('Could not fetch Yabbit domains');
    } finally {
      setLoadingDomains(false);
    }
  };

  const loadExtensions = async (domainName) => {
    if (!domainName) return;
    setLoadingExtensions(true);
    try {
      const r = await axios.get(`${API}/yabbit/domain-extensions/${domainName}`, { headers });
      setAvailableExtensions(r.data.extensions || []);
    } catch {
      setAvailableExtensions([]);
      toast.error('Could not fetch extensions');
    } finally {
      setLoadingExtensions(false);
    }
  };

  const handleDomainSelect = (domainName) => {
    setForm(f => ({ ...f, yabbit_domain: domainName }));
    loadExtensions(domainName);
  };

  const toggleExtension = (ext) => {
    setForm(f => {
      const monitored = f.extensions_monitored.includes(ext)
        ? f.extensions_monitored.filter(e => e !== ext)
        : [...f.extensions_monitored, ext];
      const classifications = { ...f.extension_classifications };
      if (!monitored.includes(ext)) delete classifications[ext];
      return { ...f, extensions_monitored: monitored, extension_classifications: classifications };
    });
  };

  const setExtClassification = (ext, callType) => {
    setForm(f => ({
      ...f,
      extension_classifications: { ...f.extension_classifications, [ext]: callType },
    }));
  };

  const handleSave = async () => {
    if (!form.yabbit_domain) { toast.error('Please select a Yabbit domain'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/domains/${domainId}`, form, { headers });
        toast.success('Domain updated');
      } else {
        await axios.post(`${API}/admin/domains`, form, { headers });
        toast.success('Domain created');
      }
      navigate('/admin/domains');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCompanyChange = (key, value) => {
    setForm(prev => ({ ...prev, company: { ...prev.company, [key]: value } }));
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return <StepCompanyDetails company={form.company} onChange={handleCompanyChange} />;
      case 1:
        return (
          <StepDomainIndustry
            form={form}
            setForm={setForm}
            yabbitDomains={yabbitDomains}
            loadYabbitDomains={loadYabbitDomains}
            loadingDomains={loadingDomains}
            onSelectDomain={handleDomainSelect}
            loadExtensions={loadExtensions}
          />
        );
      case 2:
        return (
          <StepExtensions
            form={form}
            setForm={setForm}
            availableExtensions={availableExtensions}
            loadingExtensions={loadingExtensions}
            toggleExtension={toggleExtension}
            setExtClassification={setExtClassification}
            domainId={isEdit ? domainId : null}
            token={token}
          />
        );
      case 3:
        return <StepNotifications form={form} setForm={setForm} />;
      default:
        return null;
    }
  };

  const handleNext = () => {
    // If we've just chosen a domain manually (no preloaded list) and haven't yet fetched extensions, try now.
    if (step === 1 && !loadingDomains && yabbitDomains.length === 0 && form.yabbit_domain && availableExtensions.length === 0) {
      loadExtensions(form.yabbit_domain);
    }
    setStep(s => s + 1);
  };

  return (
    <AdminLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">{isEdit ? 'Edit Domain' : 'Add New Domain'}</h1>
          <p className="text-white/40 text-sm mt-1">Set up a new customer domain for call monitoring</p>
        </div>

        <StepIndicator current={step} />

        <div className="bg-[#0A0F1C] border border-white/10 rounded-2xl p-6 mb-6">
          <h2 className="text-white font-semibold mb-5">{STEPS[step]}</h2>
          {renderStep()}
        </div>

        <div className="flex justify-between">
          <button
            onClick={() => step === 0 ? navigate('/admin/domains') : setStep(s => s - 1)}
            className="px-5 py-2.5 text-white/40 hover:text-white text-sm transition-colors"
          >
            {step === 0 ? 'Cancel' : '← Back'}
          </button>
          {step < STEPS.length - 1 ? (
            <button
              onClick={handleNext}
              className="px-6 py-2.5 bg-teal-500 hover:bg-teal-400 text-white font-medium text-sm rounded-lg transition-colors"
              data-testid="next-step-btn"
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-teal-500 hover:bg-teal-400 disabled:bg-white/10 disabled:text-white/30 text-white font-medium text-sm rounded-lg transition-colors flex items-center gap-2"
              data-testid="save-domain-btn"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Domain'}
            </button>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default DomainSetupPage;
